import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";
import { SectionHeading as HeadingTitle } from "../misc/Headings.js";
import Header, { LogoLink, NavLinks, NavLink as NavLinkBase } from "../headers/light.js";

import Banner1 from "../../images/banner.jpg";
import Banner2 from "../../images/banner2.jpg";
import Banner3 from "../../images/banner3.jpg";
import Banner4 from "../../images/banner4.jpg";
import Banner5 from "../../images/banner5.jpg";
import Banner6 from "../../images/banner6.jpeg";
import Banner7 from "../../images/banner7.jpg";

const StyledHeader = styled(Header)`
    ${tw`justify-center md:justify-start`}
    ${LogoLink} {
        ${tw`mr-8 pb-0`}
    }
`;

const ResponsiveBreak = tw.br`hidden md:block`;
const ResponsiveMobileBreak = tw.br`sm:hidden`;
const NavLink = tw(NavLinkBase)`sm:text-sm lg:text-xl text-primary-200 sm:mx-6 font-display font-light`;
const FloatingWhatsAppButton = styled.a`
    ${tw`fixed bottom-0 right-0 z-50`}
    background-color: #25d366;
    border-radius: 9999px;
    padding: 0.75rem;
    display: flex;
    align-items: center;
    margin-right: 2rem;
    margin-bottom: 2rem;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease-in-out;

    &:hover {
        transform: scale(1.05);
    }

    svg {
        width: 32px;
        height: 32px;
        color: white;
    }
`;
const Container = tw.div`relative -mx-8 -mt-8`;

const ContentWrapper = styled.div`
    ${tw`relative min-h-[60vh] flex items-center justify-center px-4`}
    background-image: url(${(props) => props.image});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: background-image 1s ease-in-out;
    position: relative;

    &::before {
        content: "";
        ${tw`absolute inset-0 bg-black bg-opacity-25`}
        z-index: 0;
    }

    & > * {
        position: relative;
        z-index: 1;
    }
`;

const Content = tw.div`lg:mb-24 flex flex-col sm:items-center justify-center min-h-[60vh] px-4 relative z-10`;

const Paragraph = tw.p`max-w-md my-2 lg:my-4 sm:text-lg lg:text-base xl:text-3xl leading-tight font-display text-white text-center`;

const Actions = styled.div`
    ${tw`flex flex-col md:flex-row gap-5 mt-6 md:mt-12 lg:mb-0`}
    .action {
        ${tw`text-center inline-block w-full sm:w-72 py-4 font-semibold tracking-wide rounded hocus:outline-none uppercase focus:shadow-outline transition duration-300`}
    }
    .primaryAction {
        ${tw`bg-primary-500 text-gray-100 hover:bg-primary-700`}
    }
    .secondaryAction {
        ${tw`sm:mt-0 bg-gray-300 text-primary-700 hover:bg-gray-400 hover:text-primary-800`}
    }
`;

export default ({
    navLinks = [
        <NavLinks key={1}>
            <NavLink href="#nosotros">Nosotros</NavLink>
            <NavLink href="#productos">Productos</NavLink>
            <NavLink href="#contacto">Contacto</NavLink>
        </NavLinks>
    ],
    heading = (
        <>
            Lo más rico <ResponsiveMobileBreak /> en tu mesa <ResponsiveBreak /> para momentos únicos.
        </>
    ),
    description = "Desde 1995, llevamos el sabor de lo casero a cada hogar.",
    primaryActionUrl = "#nosotros",
    primaryActionText = "Conocé nuestra historia",
    secondaryActionUrl = "#productos",
    secondaryActionText = "Descubrí nuestros productos"
}) => {
    const banners = [Banner1, Banner2, Banner3, Banner4, Banner5, Banner6, Banner7];
    const [currentIndex, setCurrentIndex] = useState(0);

    // Avanzar automáticamente
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % banners.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <Container>
            <StyledHeader links={navLinks} collapseBreakpointClass="sm" />

            {/* Solo cambia el fondo del ContentWrapper */}
            <ContentWrapper image={banners[currentIndex]}>
                <Content>
                    <HeadingTitle style={{ color: "#fff" }}>{heading}</HeadingTitle>
                    <Paragraph>{description}</Paragraph>
                    <Actions>
                        <a href={primaryActionUrl} className="action primaryAction">
                            {primaryActionText}
                        </a>
                        <a href={secondaryActionUrl} className="action secondaryAction">
                            {secondaryActionText}
                        </a>
                    </Actions>
                </Content>
            </ContentWrapper>

            {/* Precarga con imágenes ocultas */}
            <div style={{ display: "none" }}>
                {banners.map((src, i) => (
                    <img key={i} src={src} alt={`Preload ${i}`} />
                ))}
            </div>
            <FloatingWhatsAppButton
                href="https://wa.me/5493413096398?text=Hola%2C%20quiero%20hacer%20una%20consulta%20sobre%20sus%20productos"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
            >
                <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 48 48">
                    <path
                        fill="#fff"
                        d="M4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5c5.1,0,9.8,2,13.4,5.6	C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19c0,0,0,0,0,0h0c-3.2,0-6.3-0.8-9.1-2.3L4.9,43.3z"
                    ></path>
                    <path
                        fill="#fff"
                        d="M4.9,43.8c-0.1,0-0.3-0.1-0.4-0.1c-0.1-0.1-0.2-0.3-0.1-0.5L7,33.5c-1.6-2.9-2.5-6.2-2.5-9.6	C4.5,13.2,13.3,4.5,24,4.5c5.2,0,10.1,2,13.8,5.7c3.7,3.7,5.7,8.6,5.7,13.8c0,10.7-8.7,19.5-19.5,19.5c-3.2,0-6.3-0.8-9.1-2.3	L5,43.8C5,43.8,4.9,43.8,4.9,43.8z"
                    ></path>
                    <path
                        fill="#cfd8dc"
                        d="M24,5c5.1,0,9.8,2,13.4,5.6C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19h0c-3.2,0-6.3-0.8-9.1-2.3	L4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5 M24,43L24,43L24,43 M24,43L24,43L24,43 M24,4L24,4C13,4,4,13,4,24	c0,3.4,0.8,6.7,2.5,9.6L3.9,43c-0.1,0.3,0,0.7,0.3,1c0.2,0.2,0.4,0.3,0.7,0.3c0.1,0,0.2,0,0.3,0l9.7-2.5c2.8,1.5,6,2.2,9.2,2.2	c11,0,20-9,20-20c0-5.3-2.1-10.4-5.8-14.1C34.4,6.1,29.4,4,24,4L24,4z"
                    ></path>
                    <path
                        fill="#40c351"
                        d="M35.2,12.8c-3-3-6.9-4.6-11.2-4.6C15.3,8.2,8.2,15.3,8.2,24c0,3,0.8,5.9,2.4,8.4L11,33l-1.6,5.8	l6-1.6l0.6,0.3c2.4,1.4,5.2,2.2,8,2.2h0c8.7,0,15.8-7.1,15.8-15.8C39.8,19.8,38.2,15.8,35.2,12.8z"
                    ></path>
                    <path
                        fill="#fff"
                        fill-rule="evenodd"
                        d="M19.3,16c-0.4-0.8-0.7-0.8-1.1-0.8c-0.3,0-0.6,0-0.9,0	s-0.8,0.1-1.3,0.6c-0.4,0.5-1.7,1.6-1.7,4s1.7,4.6,1.9,4.9s3.3,5.3,8.1,7.2c4,1.6,4.8,1.3,5.7,1.2c0.9-0.1,2.8-1.1,3.2-2.3	c0.4-1.1,0.4-2.1,0.3-2.3c-0.1-0.2-0.4-0.3-0.9-0.6s-2.8-1.4-3.2-1.5c-0.4-0.2-0.8-0.2-1.1,0.2c-0.3,0.5-1.2,1.5-1.5,1.9	c-0.3,0.3-0.6,0.4-1,0.1c-0.5-0.2-2-0.7-3.8-2.4c-1.4-1.3-2.4-2.8-2.6-3.3c-0.3-0.5,0-0.7,0.2-1c0.2-0.2,0.5-0.6,0.7-0.8	c0.2-0.3,0.3-0.5,0.5-0.8c0.2-0.3,0.1-0.6,0-0.8C20.6,19.3,19.7,17,19.3,16z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </FloatingWhatsAppButton>
        </Container>
    );
};
